import {
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import type {
  Account,
  AzureServicePrincipalIdResponse,
  InventoryResource,
} from '@repo/api-gw-sdk';
import { useEffect, useState } from 'react';

import MainDialog from '@/components/dialogs/mainDialog';
import CopyableCode from '@/components/shared/copyableCode';
import { useDAL } from '@/contexts/useDAL';
import { useUser } from '@/contexts/useUser';
import { useWorkspace } from '@/contexts/useWorkspace';

import ErrorDialog from './errorDialog';

interface SqlVmActionRequiredDialogProps {
  resources: InventoryResource[];
  onClose: () => void;
  onRefreshResources?: () => Promise<void>;
}

export const SqlVmActionRequiredDialog = ({
  resources,
  onClose,
  onRefreshResources,
}: SqlVmActionRequiredDialogProps) => {
  const availableTabs = ['bash', 'terraform'];
  const [selectedTab, setSelectedTab] = useState(availableTabs[0]);
  const { user } = useUser();
  const dal = useDAL();
  const { snackbar } = useWorkspace();
  const eonAccountId = user?.eonAccount?.id || '';
  const { body: servicePrincipalIdsRes } = dal.azure.getServicePrincipalId();
  const { body: sourceAccountsRes } = dal.cloudAccounts.source.list();

  // State for the API call
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string>('');

  const onValidateConfiguration = async () => {
    setIsValidating(true);
    setValidationError('');

    try {
      const response = await dal.azure.batchValidateResourceConfiguration(
        resources.map((resource) => resource.id)
      );

      if (response.results.every((result) => result.isValid)) {
        if (onRefreshResources) {
          void onRefreshResources();
        }

        snackbar.showMessage('Configuration validated successfully.');
        onClose();
      } else {
        setValidationError(JSON.stringify(response, null, 2));
      }
    } catch (error) {
      setValidationError(
        error instanceof Error ? error.message : 'An unexpected error occurred'
      );
    } finally {
      setIsValidating(false);
    }
  };

  // Pre-compute all container names and resource data
  const [resourcesData, setResourcesData] = useState<
    {
      resource: InventoryResource;
      sourceAccount?: Account;
      resourceGroupName: string;
      storageAccountName: string;
      containerName: string;
      sqlVmResourceId: string;
    }[]
  >([]);
  const [isLoadingContainerNames, setIsLoadingContainerNames] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const computeResourcesData = async () => {
      try {
        const dataPromises = resources.map(async (resource) => {
          const sourceAccount = getSourceAccountForResource(
            resource,
            sourceAccountsRes?.accounts
          );
          const resourceGroupName = getResourceGroupName(
            resource,
            sourceAccount
          );
          const containerName = await generateStableContainerName(resource);
          return {
            resource,
            sourceAccount,
            resourceGroupName,
            storageAccountName: await generateStorageAccountName(
              eonAccountId,
              resource.providerAccountId,
              resource.region
            ),
            containerName,
            sqlVmResourceId: buildSqlVmResourceId(resource),
          };
        });

        const computedData = await Promise.all(dataPromises);

        if (!cancelled) {
          setResourcesData(computedData);
          setIsLoadingContainerNames(false);
        }
      } catch (error) {
        console.error('Error computing resources data', error);
        if (!cancelled) {
          setIsLoadingContainerNames(false);
        }
      }
    };

    void computeResourcesData();

    return () => {
      cancelled = true;
    };
  }, [resources, eonAccountId, sourceAccountsRes]);

  const loadingTabData: Record<string, TabData> = {
    bash: { title: 'Bash', description: 'Loading...', codeTitle: '', code: '' },
    terraform: {
      title: 'Terraform',
      description: 'Loading...',
      codeTitle: '',
      code: '',
    },
  };

  const tabData = isLoadingContainerNames
    ? loadingTabData
    : getTabData(resourcesData, eonAccountId, servicePrincipalIdsRes);

  const dialogTitle =
    resources.length > 1
      ? 'Complete resources configuration'
      : 'Complete resource configuration';

  return (
    <>
      <MainDialog
        isOpen={!validationError}
        isLoading={isLoadingContainerNames}
        header={{
          title: dialogTitle,
          subtitle: (
            <Stack direction='row' spacing={8} className='flex justify-between'>
              <Typography>{tabData[selectedTab].description}</Typography>
              <ToggleButtonGroup
                exclusive
                value={selectedTab}
                onChange={(e, value) => {
                  if (value === null) {
                    // Keep the current selection
                    return;
                  }
                  setSelectedTab(value);
                }}
              >
                <ToggleButton value='bash'>Bash</ToggleButton>
                <ToggleButton value='terraform'>Terraform</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          ),
          fullWidthSubtitle: true,
          alignTitles: true,
          onClose,
        }}
        footer={{
          primaryButton: {
            text: 'Validate Configuration',
            onClick: onValidateConfiguration,
            'data-testid': 'sqlvm-action-required-dialog-validate-button',
          },
          secondaryButton: {
            text: 'Cancel',
            onClick: onClose,
            'data-testid': 'sqlvm-action-required-dialog-cancel-button',
          },
          hideButtons: isLoadingContainerNames,
          submitInProgress: isValidating,
          loadingText: 'Checking configuration...',
        }}
        sx={{
          width: '60%',
          minWidth: '800px',
          maxHeight: '90vh',
          overflowY: 'hidden',
        }}
      >
        <Stack className='px-[24px] py-0'>
          {!isLoadingContainerNames && (
            <CopyableCode
              code={tabData[selectedTab].code}
              title={tabData[selectedTab].codeTitle}
              codeClassName='overflow-y-auto max-h-[30vh]'
              tooltipText='Applying this configuration will enable Point-in-Time Restore (PiTR) backups for this SQL VM resource in Eon.'
            />
          )}
        </Stack>
      </MainDialog>
      <ErrorDialog
        isOpen={!!validationError}
        title='Couldn’t validate configuration'
        description='Check the error details and contact support if needed.'
        onClose={onClose}
        error={validationError}
      />
    </>
  );
};

interface TabData {
  title: string;
  description: string;
  codeTitle: string;
  code: string;
}

const getTabData = (
  resourcesData: {
    resource: InventoryResource;
    sourceAccount?: Account;
    resourceGroupName: string;
    storageAccountName: string;
    containerName: string;
    sqlVmResourceId: string;
  }[],
  eonAccountId: string,
  servicePrincipalIdsRes?: AzureServicePrincipalIdResponse
): Record<string, TabData> => {
  const resourcesBySubscriptionAndRegion = resourcesData.reduce<
    Record<string, typeof resourcesData>
  >((acc, data) => {
    const key = `${data.resource.providerAccountId}|${data.resource.region}`;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(data);
    return acc;
  }, {});

  const resourceCount = resourcesData.length;
  const isMultiple = resourceCount > 1;

  return {
    bash: {
      title: 'Bash',
      description: `Copy and run the following bash commands in order to enable Eon PiTR backups for your SQL VM${isMultiple ? 's' : ''}. Once you're done, click the button below to validate the configuration.`,
      codeTitle: 'main.sh',
      code: generateBashScript(
        resourcesData,
        resourcesBySubscriptionAndRegion,
        eonAccountId,
        servicePrincipalIdsRes
      ),
    },
    terraform: {
      title: 'Terraform',
      description: `Review and deploy the Terraform configuration in your cloud account to enable Eon PiTR backups for your SQL VM${isMultiple ? 's' : ''}. Once you're done, click the button below to validate it.`,
      codeTitle: 'main.tf',
      code: generateTerraformScript(
        resourcesData,
        eonAccountId,
        servicePrincipalIdsRes
      ),
    },
  };
};

const generateBashScript = (
  resourcesData: {
    resource: InventoryResource;
    sourceAccount?: Account;
    resourceGroupName: string;
    storageAccountName: string;
    containerName: string;
    sqlVmResourceId: string;
  }[],
  resourcesBySubscriptionAndRegion: Record<string, typeof resourcesData>,
  eonAccountId: string,
  servicePrincipalIdsRes?: AzureServicePrincipalIdResponse
): string => {
  const subscriptionRegionKeys = Object.keys(resourcesBySubscriptionAndRegion);

  // Generate arrays for all resources
  const subscriptionIds = resourcesData.map(
    (d) => d.resource.providerAccountId
  );
  const containerNames = resourcesData.map((d) => d.containerName);
  const resourceGroups = resourcesData.map((d) => d.resourceGroupName);
  const sqlVmNames = resourcesData.map((d) => d.resource.resourceName);
  const sqlVmResourceIds = resourcesData.map((d) => d.sqlVmResourceId);
  const regions = resourcesData.map((d) => d.resource.region);
  const storageAccountNames = resourcesData.map((d) => d.storageAccountName);

  // Create mapping from subscription+region to storage account
  const subscriptionRegionToStorageAccount = subscriptionRegionKeys.reduce<
    Record<string, string>
  >((acc, key) => {
    const firstResourceInGroup = resourcesBySubscriptionAndRegion[key][0];
    acc[key] = firstResourceInGroup.storageAccountName;
    return acc;
  }, {});

  return `# Create the bash script file
cat > configure-sql-vms.sh << 'SCRIPT_EOF'
#!/bin/bash
set -e  # Exit on any error

# Arrays for all resources
SUBSCRIPTION_IDS=(${subscriptionIds.map((id) => `"${id}"`).join(' ')})
CONTAINER_NAMES=(${containerNames.map((name) => `"${name}"`).join(' ')})
RESOURCE_GROUPS=(${resourceGroups.map((rg) => `"${rg}"`).join(' ')})
SQL_VM_NAMES=(${sqlVmNames.map((name) => `"${name}"`).join(' ')})
SQL_VM_RESOURCE_IDS=(${sqlVmResourceIds.map((id) => `"${id}"`).join(' ')})
REGIONS=(${regions.map((region) => `"${region}"`).join(' ')})
STORAGE_ACCOUNT_NAMES=(${storageAccountNames.map((name) => `"${name}"`).join(' ')})

# Get unique subscription+region combinations for storage account creation
SUBSCRIPTION_REGION_KEYS=(${subscriptionRegionKeys.map((key) => `"${key}"`).join(' ')})

# Subscription+region to storage account mapping
declare -A SUBSCRIPTION_REGION_TO_STORAGE_ACCOUNT
${Object.entries(subscriptionRegionToStorageAccount)
  .map(
    ([key, storageAccountName]) =>
      `SUBSCRIPTION_REGION_TO_STORAGE_ACCOUNT["${key}"]="${storageAccountName}"`
  )
  .join('\n')}

# Resource count
RESOURCE_COUNT=${resourcesData.length}

echo "Configuring $RESOURCE_COUNT SQL VM(s) across ${subscriptionRegionKeys.length} subscription+region combination(s)"

# Step 1: Create storage accounts (one per unique subscription+region combination)
for SUB_REGION_KEY in "\${SUBSCRIPTION_REGION_KEYS[@]}"; do
  IFS='|' read -r SUB_ID REGION <<< "$SUB_REGION_KEY"
  echo "Processing subscription: $SUB_ID, region: $REGION"
  az account set --subscription $SUB_ID

  # Find first resource in this subscription+region combination to get resource group
  for i in $(seq 0 $((RESOURCE_COUNT-1))); do
    if [ "\${SUBSCRIPTION_IDS[i]}" = "$SUB_ID" ] && [ "\${REGIONS[i]}" = "$REGION" ]; then
      # Get storage account name from mapping
      STORAGE_NAME=\${SUBSCRIPTION_REGION_TO_STORAGE_ACCOUNT["$SUB_REGION_KEY"]}
      echo "Creating storage account $STORAGE_NAME for subscription $SUB_ID in region $REGION"

      az storage account create \\
        --name $STORAGE_NAME \\
        --resource-group "\${RESOURCE_GROUPS[i]}" \\
        --location "$REGION" \\
        --sku Standard_LRS \\
        --tags "eon:storage-role=sql-vm-pitr" "eon:eon-account-id=${eonAccountId}" \\
        --only-show-errors

      # Grant Eon management app permissions
      az role assignment create \\
        --assignee ${servicePrincipalIdsRes?.managementServicePrincipalId || '<eon-management-service-principal-id>'} \\
        --role "Storage Blob Data Contributor" \\
        --scope "/subscriptions/$SUB_ID/resourceGroups/\${RESOURCE_GROUPS[i]}/providers/Microsoft.Storage/storageAccounts/$STORAGE_NAME" \\
        --only-show-errors

      # Grant Eon data access app permissions
      az role assignment create \\
        --assignee ${servicePrincipalIdsRes?.dataAccessServicePrincipalId || '<eon-data-access-service-principal-id>'} \\
        --role "Storage Blob Data Contributor" \\
        --scope "/subscriptions/$SUB_ID/resourceGroups/\${RESOURCE_GROUPS[i]}/providers/Microsoft.Storage/storageAccounts/$STORAGE_NAME" \\
        --only-show-errors

      break
    fi
  done
done

# Step 2: Process each SQL VM
for i in $(seq 0 $((RESOURCE_COUNT-1))); do
  echo "Configuring SQL VM \${SQL_VM_NAMES[i]} ($(($i+1))/$RESOURCE_COUNT)"

  az account set --subscription \${SUBSCRIPTION_IDS[i]}

  # Use the pre-computed storage account name for this resource
  STORAGE_NAME=\${STORAGE_ACCOUNT_NAMES[i]}

  # Create blob container for this SQL VM
  az storage container create \\
    --name \${CONTAINER_NAMES[i]} \\
    --account-name $STORAGE_NAME \\
    --resource-group \${RESOURCE_GROUPS[i]} \\
    --metadata "sql_vm_resource_id=\${SQL_VM_RESOURCE_IDS[i]}" \\
    --only-show-errors
done

# Step 3: Deploy ARM templates in parallel for all SQL VMs
echo "🚀 Starting parallel deployment of $RESOURCE_COUNT SQL VM(s)..."

for i in $(seq 0 $((RESOURCE_COUNT-1))); do
  {
    echo "⏳ [$(date '+%H:%M:%S')] Starting deployment for SQL VM \${SQL_VM_NAMES[i]} ($(($i+1))/$RESOURCE_COUNT)"

    az account set --subscription \${SUBSCRIPTION_IDS[i]}

    # Use the pre-computed storage account name for this resource
    STORAGE_NAME=\${STORAGE_ACCOUNT_NAMES[i]}

    # Create unique ARM template file for this SQL VM
    VM_HASH=$(echo "\${SQL_VM_RESOURCE_IDS[i]}" | cksum | cut -d' ' -f1)
    TEMPLATE_FILE="sql-vm-template-$VM_HASH.json"
    cat > $TEMPLATE_FILE << 'TEMPLATE_EOF'
{
"$schema": "https://schema.management.azure.com/schemas/2019-04-01/deploymentTemplate.json#",
"contentVersion": "1.0.0.0",
"parameters": {
  "location": { "type": "string" },
  "virtualMachineName": { "type": "string" },
  "storageAccountName": { "type": "string" },
  "storageContainerName": { "type": "string" },
  "resourceGroupName": { "type": "string" }
},
"resources": [
  {
    "type": "Microsoft.SqlVirtualMachine/SqlVirtualMachines",
    "apiVersion": "2022-07-01-preview",
    "name": "[parameters('virtualMachineName')]",
    "location": "[parameters('location')]",
    "properties": {
      "virtualMachineResourceId": "[resourceId('Microsoft.Compute/virtualMachines', parameters('virtualMachineName'))]",
      "AutoBackupSettings": {
        "Enable": true,
        "RetentionPeriod": "7",
        "StorageAccountUrl": "[reference(resourceId(parameters('resourceGroupName'), 'Microsoft.Storage/storageAccounts', parameters('storageAccountName')), '2019-06-01').primaryEndpoints.blob]",
        "StorageAccessKey": "[first(listKeys(resourceId(parameters('resourceGroupName'), 'Microsoft.Storage/storageAccounts', parameters('storageAccountName')), '2019-06-01').keys).value]",
        "StorageContainerName": "[parameters('storageContainerName')]",
        "EnableEncryption": false,
        "BackupSystemDbs": true,
        "BackupScheduleType": "Manual",
        "FullBackupFrequency": "Daily",
        "FullBackupStartTime": "0",
        "FullBackupWindowHours": "4",
        "LogBackupFrequency": "5"
      }
    }
  }
]
}
TEMPLATE_EOF

    az deployment group create \\
      --resource-group \${RESOURCE_GROUPS[i]} \\
      --template-file $TEMPLATE_FILE \\
      --parameters \\
        location="\${REGIONS[i]}" \\
        virtualMachineName="\${SQL_VM_NAMES[i]}" \\
        storageAccountName="$STORAGE_NAME" \\
        storageContainerName="\${CONTAINER_NAMES[i]}" \\
        resourceGroupName="\${RESOURCE_GROUPS[i]}" \\
      --mode Incremental \\
      --only-show-errors

    # Clean up the template file for this VM
    rm $TEMPLATE_FILE

    echo "✅ [$(date '+%H:%M:%S')] Completed deployment for SQL VM \${SQL_VM_NAMES[i]}"
  } &
done

echo "⏳ Waiting for all ARM template deployments to complete..."
wait

echo "🎉 Configuration complete for all $RESOURCE_COUNT SQL VM(s) - deployed in parallel!"

SCRIPT_EOF

# Make it executable and run with bash
chmod +x configure-sql-vms.sh
bash configure-sql-vms.sh

# Clean up the script file
rm configure-sql-vms.sh`;
};

const generateTerraformScript = (
  resourcesData: {
    resource: InventoryResource;
    sourceAccount?: Account;
    resourceGroupName: string;
    storageAccountName: string;
    containerName: string;
    sqlVmResourceId: string;
  }[],
  eonAccountId: string,
  servicePrincipalIdsRes?: AzureServicePrincipalIdResponse
): string => {
  // Convert resources data to Terraform format
  const resourcesForTerraform = resourcesData.map((data, index) => ({
    key: `resource_${index}`,
    subscription_id: data.resource.providerAccountId,
    resource_group: data.resourceGroupName || '<resource-group>',
    vm_name: data.resource.resourceName || '<sql-vm-name>',
    region: data.resource.region,
    storage_account: data.storageAccountName,
    container_name: data.containerName,
    sql_vm_resource_id: data.sqlVmResourceId,
  }));

  // Group by subscription+region for storage accounts
  const storageAccountsBySubscriptionRegion = resourcesData.reduce<
    Record<
      string,
      {
        name: string;
        resource_group: string;
        region: string;
      }
    >
  >((acc, data) => {
    const key = `${data.resource.providerAccountId}|${data.resource.region}`;
    if (!acc[key]) {
      acc[key] = {
        name: data.storageAccountName,
        resource_group: data.resource.resourceGroupName || '<resource-group>',
        region: data.resource.region,
      };
    }
    return acc;
  }, {});

  return `terraform {
  required_providers {
    azurerm = {
      source  = "hashicorp/azurerm"
      version = "~>3.0"
    }
    azapi = {
      source  = "azure/azapi"
      version = "~>1.0"
    }
  }
}

# Configure the Microsoft Azure Provider
provider "azurerm" {
  features {}
}

# Configure the Azure API Provider
provider "azapi" {}

locals {
  # Resource configuration
  sql_vm_resources = {
${resourcesForTerraform
  .map(
    (r) => `    "${r.key}" = {
      subscription_id       = "${r.subscription_id}"
      resource_group        = "${r.resource_group}"
      vm_name               = "${r.vm_name}"
      region                = "${r.region}"
      storage_account       = "${r.storage_account}"
      container_name        = "${r.container_name}"
      sql_vm_resource_id    = "${r.sql_vm_resource_id}"
    }`
  )
  .join('\n')}
  }

  # Group by subscription+region for storage accounts
  storage_accounts = {
${Object.entries(storageAccountsBySubscriptionRegion)
  .map(
    ([key, account]) => `    "${key}" = {
      name           = "${account.name}"
      resource_group = "${account.resource_group}"
      region         = "${account.region}"
    }`
  )
  .join('\n')}
  }
}

# Get current subscription info
data "azurerm_client_config" "current" {}

# Storage accounts (one per subscription+region combination)
resource "azurerm_storage_account" "sql_vm_backup" {
  for_each = local.storage_accounts

  name                     = each.value.name
  resource_group_name      = each.value.resource_group
  location                 = each.value.region
  account_tier             = "Standard"
  account_replication_type = "LRS"

  tags = {
    "eon:storage-role"   = "sql-vm-pitr"
    "eon:eon-account-id" = "${eonAccountId}"
  }
}

# Grant Eon management app permissions to storage accounts
resource "azurerm_role_assignment" "eon_management_storage_access" {
  for_each = local.storage_accounts

  scope                = azurerm_storage_account.sql_vm_backup[each.key].id
  role_definition_name = "Storage Blob Data Contributor"
  principal_id         = "${servicePrincipalIdsRes?.managementServicePrincipalId || '<eon-management-service-principal-id>'}"
}

# Grant Eon data access app permissions to storage accounts
resource "azurerm_role_assignment" "eon_data_access_storage_access" {
  for_each = local.storage_accounts

  scope                = azurerm_storage_account.sql_vm_backup[each.key].id
  role_definition_name = "Storage Blob Data Contributor"
  principal_id         = "${servicePrincipalIdsRes?.dataAccessServicePrincipalId || '<eon-data-access-service-principal-id>'}"
}

# Create blob containers (one per SQL VM)
resource "azurerm_storage_container" "sql_vm_backup_container" {
  for_each = local.sql_vm_resources

  name                  = each.value.container_name
  storage_account_name  = each.value.storage_account
  container_access_type = "private"

  metadata = {
    "sql_vm_resource_id" = each.value.sql_vm_resource_id
  }

  depends_on = [azurerm_storage_account.sql_vm_backup]
}

# Reference existing VMs
data "azurerm_virtual_machine" "existing_vm" {
  for_each = local.sql_vm_resources

  name                = each.value.vm_name
  resource_group_name = each.value.resource_group
}

# Configure SQL VM auto backup using Azure API directly
resource "azapi_resource" "sql_vm_autobackup" {
  for_each = local.sql_vm_resources

  type      = "Microsoft.SqlVirtualMachine/sqlVirtualMachines@2022-07-01-preview"
  name      = each.value.vm_name
  parent_id = "/subscriptions/\${each.value.subscription_id}/resourceGroups/\${each.value.resource_group}"

  body = jsonencode({
    location = each.value.region
    properties = {
      virtualMachineResourceId = data.azurerm_virtual_machine.existing_vm[each.key].id
      autoBackupSettings = {
        enable                = true
        retentionPeriod      = 7
        storageAccountUrl    = azurerm_storage_account.sql_vm_backup["\${each.value.subscription_id}|\${each.value.region}"].primary_blob_endpoint
        storageAccessKey     = azurerm_storage_account.sql_vm_backup["\${each.value.subscription_id}|\${each.value.region}"].primary_access_key
        storageContainerName = each.value.container_name
        enableEncryption     = false
        backupSystemDbs      = true
        backupScheduleType   = "Manual"
        fullBackupFrequency  = "Daily"
        fullBackupStartTime  = 0
        fullBackupWindowHours = 4
        logBackupFrequency   = 5
      }
    }
  })

  depends_on = [
    azurerm_role_assignment.eon_management_storage_access,
    azurerm_role_assignment.eon_data_access_storage_access,
    azurerm_storage_container.sql_vm_backup_container,
  ]
}`;
};

const generateStorageAccountName = async (
  eonAccountId: string,
  subscriptionId: string,
  region: string
) => {
  const eonIdSuffix = eonAccountId.substring(0, 6) || 'xxxxxx';
  const combinedInput = `${subscriptionId}|${region}`;
  const hashHex = await sha256Hex(combinedInput);
  const hashSuffix = hashHex.substring(0, 10);
  return `eonsqlbk${eonIdSuffix}${hashSuffix}`;
};

const buildSqlVmResourceId = (resource: InventoryResource): string => {
  return `/subscriptions/${resource.providerAccountId}/resourceGroups/${resource.resourceGroupName}/providers/Microsoft.SqlVirtualMachine/SqlVirtualMachines/${resource.resourceName}`;
};

const generateStableContainerName = async (
  resource: InventoryResource
): Promise<string> => {
  const stableInput = `${resource.providerAccountId}-${resource.resourceGroupName}-${resource.resourceName}`;

  const hashId = (await sha256Hex(stableInput)).substring(0, 12);

  // Format: sqlvm-backup-{hash12chars}
  // Starts with letter, ends with alphanumeric, single hyphens only
  return `sqlvm-backup-${hashId}`;
};

const sha256Hex = async (str: string): Promise<string> => {
  const data = new TextEncoder().encode(str);
  const buf = await crypto.subtle.digest('SHA-256', data);
  return [...new Uint8Array(buf)]
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
};

const getSourceAccountForResource = (
  resource: InventoryResource,
  sourceAccounts?: Account[]
): Account | undefined => {
  if (!sourceAccounts) return undefined;
  return sourceAccounts.find(
    (account) => account.providerAccountId === resource.providerAccountId
  );
};

const getResourceGroupName = (
  resource: InventoryResource,
  sourceAccount?: Account
): string => {
  // Use eon internal resource group if available
  if (sourceAccount?.eonInternalAzureResourceGroupName?.trim()) {
    return sourceAccount.eonInternalAzureResourceGroupName;
  }
  // Fallback to SQL VM's resource group (current behavior)
  return resource.resourceGroupName || '';
};
